import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import moment from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'

import TimesheetButtons from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/TimesheetButtons'
import CounterWidget from 'modules/attendance/components/ClockIn/Shifts/DailyShifts/DailyCounter/CounterWidget'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { useSubmitBulkChange } from 'modules/attendance/hooks/shifts/useSubmitBulkChange'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { trackDailyClockIn } from 'modules/attendance/lib/tracking'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'

import DailyCounter from './DailyCounter'
import DailyTable from './DailyTable'

interface Props {
  store: Store
  showHourBalance: boolean
}

const DailyShifts = ({ store, showHourBalance }: Props) => {
  const { deleteShift, createShift } = useShiftActions()
  const { currentMonth } = store

  const { shifts, reviewsStats, attendancePeriods, featureByName, loading } = useTimesheetData()

  const { setHalfDayShiftCount, halfDayShiftCount } = useSubmitBulkChange({
    year: store.year,
    employeeId: store.employeeId,
    currentMonth,
    shifts,
  })

  const submitHalfDayShift = async (shift: TimesheetPageShiftFragment) => {
    if (!AttendanceShiftUtils.isNew(shift)) {
      const { success } = await asyncAction(
        () =>
          deleteShift({
            id: shift.id,
            date: shift.referenceDate,
            fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName),
          }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      if (success) {
        setHalfDayShiftCount((prev) => prev - AttendanceShiftUtils.halfDayDuration(shift))
      }
    } else {
      const { success } = await asyncAction(
        () =>
          createShift({
            date: shift.date,
            referenceDate: shift.date,
            halfDay: shift.halfDay,
            employeeId: store.employeeId,
            workable: true,
            fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName),
          }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      if (success) {
        setHalfDayShiftCount((prev) => prev + AttendanceShiftUtils.halfDayDuration(shift))
        trackDailyClockIn('timesheet_daily_desktop')
      }
    }
  }

  const { dateFrom, dateTo } = useAttendanceDateNavigator()
  const rangeDays = AttendanceShiftUtils.daysBetweenDates(moment(dateFrom), moment(dateTo))
  const FJffenabled = false // will be replaced with the new feature flag for FJ sidepanel

  const { contract } = useContractsVersion()

  const total = contract?.frJoursParAn ?? 0
  const balance = total - halfDayShiftCount
  const remainingWorkDays = balance < 0 ? 0 : balance
  const year = currentMonth.year()

  return (
    <>
      <Box paddingBottom="s24">
        {FJffenabled ? (
          <CounterWidget loading={loading} remainingWorkDays={remainingWorkDays} year={year} />
        ) : (
          <DailyCounter
            year={year}
            remainingWorkDays={remainingWorkDays}
            totalWorkedDays={halfDayShiftCount}
            total={total}
          />
        )}
      </Box>

      <TimesheetButtons employeeId={store.employeeId} showHourBalance={showHourBalance} isDaily />

      <DailyTable
        store={store}
        submitHalfDayShift={submitHalfDayShift}
        shiftsHash={AttendanceShiftUtils.halfDayShifts({
          attendancePeriods,
          employeeId: store.employeeId,
          shifts,
          rangeDays,
        })}
        rangeDays={rangeDays}
        reviewsStats={reviewsStats}
      />
    </>
  )
}

export default DailyShifts
